import { hash } from '@ember/helper';
import Component from '@glimmer/component';
import type { ComponentLike, WithBoundArgs } from '@glint/template';
import UiCard from 'garaje/components/ui/card';

import Body from './settings-panel/sp-body';
import BodyStates from './settings-panel/sp-body-states';
import type { SettingsPanelFooterSignature } from './settings-panel/sp-footer';
import Footer from './settings-panel/sp-footer';
import Header from './settings-panel/sp-header';
import Skeleton from './settings-panel/sp-skeleton';

interface SettingsPanelSignature {
  Args: {
    cancelText?: string;
    isEnabled?: boolean;
    isExpandable?: boolean;
    isInactive?: boolean;
    /** Renders a placeholder header instead of yielding, so callers need no undefined-data guards. */
    isLoading?: boolean;
    isOpen?: boolean;
    onCancel?: () => void;
    showCancel?: boolean;
    toEditing?: () => void;
    toPreviewing?: () => void;
    twoTone?: boolean;
  };
  Blocks: {
    default: [
      {
        header: WithBoundArgs<typeof Header, 'isOpen' | 'onCancel' | 'twoTone' | 'cancelText' | 'showCancel'>;
        body: WithBoundArgs<typeof Body, 'isOpen'>;
        bodyStates?: WithBoundArgs<typeof BodyStates, 'isOpen' | 'toPreviewing' | 'toEditing'>;
        footer?: ComponentLike<SettingsPanelFooterSignature>;
        isOpen?: boolean;
      },
    ];
  };
  Element: HTMLDivElement;
}
export default class SettingsPanel extends Component<SettingsPanelSignature> {
  get isExpandable(): boolean {
    return this.args.isExpandable ?? true;
  }

  <template>
    <UiCard
      class="{{if @isInactive 'pointer-events-none opacity-50'}} {{if @twoTone '!p-0 !bg-carbon-5'}} "
      aria-busy={{if @isLoading "true"}}
      data-test-settings-panel
      ...attributes
    >
      {{#if @isLoading}}
        <Skeleton />
      {{else if this.isExpandable}}
        {{yield
          (hash
            header=(component
              Header isOpen=@isOpen onCancel=@onCancel twoTone=@twoTone cancelText=@cancelText showCancel=@showCancel
            )
            body=(component Body isOpen=@isOpen)
            bodyStates=(component BodyStates isOpen=@isOpen toPreviewing=@toPreviewing toEditing=@toEditing)
            footer=(component Footer)
            isOpen=@isOpen
          )
        }}
      {{else}}
        {{yield
          (hash
            header=(component
              Header isOpen=@isOpen onCancel=@onCancel twoTone=@twoTone cancelText=@cancelText showCancel=@showCancel
            )
            body=(component Body isOpen=@isOpen)
          )
        }}
      {{/if}}
    </UiCard>
  </template>
}
